import { useEffect } from 'react';
import { ProcessingProgress, isDocumentInFlight, type RoomDocument } from '@dataroom/shared';
import { useProcessingEstimate } from '@/api/hooks';

/**
 * The Processing page's estimate panel ("About 4 minutes left", Filing / Reading
 * scans / Redaction), on the room's Data room view too (owner ask 2026-10-26): it
 * shows while documents are still in the pipeline, and while an upload batch is
 * open (`batchOpen` — then also its "All documents processed" line), and is gone
 * once nothing is left. Nothing is stored: leaving the view removes it.
 *
 * The estimate polls itself while work remains; when it last read "nothing left"
 * but the room's listing shows a document in a stage (a new batch just landed),
 * it is read again — the same re-read the Processing page does.
 */
export function RoomProcessingEstimate({
  roomId,
  documents,
  batchOpen = false,
}: {
  roomId: string;
  documents: readonly RoomDocument[] | undefined;
  batchOpen?: boolean;
}) {
  const estimate = useProcessingEstimate(roomId);
  const refetch = estimate.refetch;
  const left = estimate.data?.documentsLeft;
  const running = (documents ?? []).some((d) => isDocumentInFlight(d));
  useEffect(() => {
    if (running && (left === undefined || left === 0)) void refetch();
  }, [running, left, refetch]);
  if (!batchOpen && !(left !== undefined && left > 0)) return null;
  return <ProcessingProgress estimate={estimate.data} />;
}
